import { useAudioStore } from '@/store/audioStore'
import {
  MUSIC_LIBRARY, MUSIC_CATEGORIES, ALL_CATEGORY, getTracksByCategory,
} from '@/config/musicLibrary'
import {
  Play, Pause, SkipForward, SkipBack, Volume2, Sparkles, Music,
} from 'lucide-react'

function VolumeSlider({ label, value, onChange }: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
      <span className="meta-text" style={{ minWidth: '74px' }}>{label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ flex: 1, accentColor: 'var(--gold)' }}
      />
      <span className="meta-text" style={{ minWidth: '34px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(value * 100)}%
      </span>
    </div>
  )
}

/** Music library: now-playing, category filter and the (filtered) track list. */
export function MusicLibrarySettings() {
  const {
    currentTrackId, currentCategoryId, isMusicPlaying, miniPlayerHidden,
    playTrackById, setCategory, togglePlay, nextTrack, prevTrack,
    showMiniPlayer, hideMiniPlayer,
  } = useAudioStore()

  const current = MUSIC_LIBRARY.find(t => t.id === currentTrackId) ?? MUSIC_LIBRARY[0]
  const visibleTracks = getTracksByCategory(currentCategoryId)

  return (
    <>
      {/* Now playing */}
      <div className="music-now">
        <div className="music-now-disc" data-spinning={isMusicPlaying ? 'true' : 'false'}>
          <Music size={22} />
        </div>
        <div className="music-now-info">
          <div className="card-title" style={{ fontSize: '0.95rem' }}>{current.title}</div>
          <div className="meta-text">{current.artist}</div>
          <span className="chip-angled tone-gold" style={{ marginTop: '4px' }}>{current.mood}</span>
        </div>
        <div className="music-controls">
          <button className="icon-btn" onClick={prevTrack} title="Anterior"><SkipBack size={16} /></button>
          <button className="music-play-btn" onClick={togglePlay} title={isMusicPlaying ? 'Pausar' : 'Reproducir'}>
            {isMusicPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
          <button className="icon-btn" onClick={nextTrack} title="Siguiente"><SkipForward size={16} /></button>
        </div>
      </div>

      {/* On-screen player visibility (hide it to reclaim space; music keeps playing) */}
      <button
        className="row-tile"
        onClick={miniPlayerHidden ? showMiniPlayer : hideMiniPlayer}
        style={{ justifyContent: 'space-between', marginTop: '0.75rem' }}
      >
        <span className="row-tile-title">Reproductor en pantalla</span>
        <span className={`pill ${miniPlayerHidden ? 'pill-warning' : 'pill-success'}`}>
          {miniPlayerHidden ? 'Oculto' : 'Visible'}
        </span>
      </button>

      {/* Category filter (the active playlist scope for next/prev) */}
      {MUSIC_CATEGORIES.length > 1 && (
        <div className="music-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.75rem' }}>
          <button
            className={`chip-angled ${currentCategoryId === ALL_CATEGORY ? 'tone-gold' : ''}`}
            onClick={() => setCategory(ALL_CATEGORY)}
          >
            Todas
          </button>
          {MUSIC_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              className={`chip-angled ${currentCategoryId === cat.id ? 'tone-gold' : ''}`}
              onClick={() => setCategory(cat.id)}
              title={cat.description}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Track list (filtered by the active category) */}
      <div className="space-y-2" style={{ marginTop: '0.75rem' }}>
        {visibleTracks.map(track => {
          const active = track.id === currentTrackId
          return (
            <button
              key={track.id}
              className="row-tile"
              onClick={() => playTrackById(track.id)}
              style={active ? { border: '1px solid color-mix(in srgb, var(--gold) 45%, transparent)', background: 'var(--gold-fill)' } : undefined}
            >
              <span className="music-track-num">
                {active && isMusicPlaying ? <Volume2 size={14} style={{ color: 'var(--gold)' }} /> : <Music size={14} style={{ color: 'var(--text-muted)' }} />}
              </span>
              <div className="row-tile-main">
                <div className="row-tile-title" style={active ? { color: 'var(--gold)' } : undefined}>{track.title}</div>
                <div className="row-tile-sub">{track.mood} · {track.artist}</div>
              </div>
              {active && <span className="pill pill-success">{isMusicPlaying ? 'Sonando' : 'Activa'}</span>}
            </button>
          )
        })}
      </div>

      <div className="hint-box" style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.4rem' }}>
        <Sparkles size={13} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: '2px' }} />
        <span>
          La banda sonora se genera en tu navegador (libre de derechos). Puedes
          añadir tus propias pistas sin copyright en <code>public/music/</code> —
          mira el README de esa carpeta para descargar temas de Pixabay.
        </span>
      </div>
    </>
  )
}

/** Volume sliders + music/sfx enable toggles. */
export function VolumeSettings() {
  const {
    masterVolume, musicVolume, sfxVolume, musicEnabled, sfxEnabled,
    setMasterVol, setMusicVol, setSfxVol, toggleMusic, toggleSfx,
  } = useAudioStore()

  return (
    <>
      <div className="space-y-2" style={{ padding: '0.25rem 0' }}>
        <VolumeSlider label="General" value={masterVolume} onChange={setMasterVol} />
        <VolumeSlider label="Música" value={musicVolume} onChange={setMusicVol} />
        <VolumeSlider label="Efectos" value={sfxVolume} onChange={setSfxVol} />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        <button
          className="row-tile"
          onClick={toggleMusic}
          style={{ flex: 1, minWidth: '140px', justifyContent: 'space-between' }}
        >
          <span className="row-tile-title">Música</span>
          <span className={`pill ${musicEnabled ? 'pill-success' : 'pill-warning'}`}>
            {musicEnabled ? 'Activada' : 'Silenciada'}
          </span>
        </button>
        <button
          className="row-tile"
          onClick={toggleSfx}
          style={{ flex: 1, minWidth: '140px', justifyContent: 'space-between' }}
        >
          <span className="row-tile-title">Efectos de sonido</span>
          <span className={`pill ${sfxEnabled ? 'pill-success' : 'pill-warning'}`}>
            {sfxEnabled ? 'Activados' : 'Silenciados'}
          </span>
        </button>
      </div>
    </>
  )
}
